import { inject } from '@angular/core';
import { HttpEvent, HttpRequest, HttpHandlerFn, HttpResponse } from '@angular/common/http';
import { Observable, of, delay } from 'rxjs';
import { EnvironmentService } from '../services/environment.service';
import { debugLog } from '../utils/debug-logger';
import { PortalStatus } from '../../features/admin-dashboard/models/portal-status.enum';
import type {
  IPortalConfiguration,
  IAvailableFormsResponse,
  IPublishResult,
  ILogoUploadResult,
  IPortalPreview,
} from '../../features/admin-dashboard/models/portal-configuration.model';
import type {
  IFaqEntry,
  IAdminFaqList,
  ILanguage,
} from '../../features/admin-dashboard/models/faq.model';

// ─── Dynamic mock helpers ─────────────────────────────────────────────────────

/** Extracts customerId from paths like /api/v1/admin/customers/{id}/... */
function extractCustomerId(path: string): string {
  return /\/customers\/([^/]+)\//.exec(path)?.[1] ?? 'DEMO';
}

function buildPortalConfig(customerId: string): IPortalConfiguration {
  const subdomain = `${customerId.toLowerCase()}-portal`;
  return {
    customerId,
    portalStatus: PortalStatus.Inactive,
    logoFileName: null,
    logoUrl: null,
    logoFileSize: null,
    logoUploadedAt: null,
    logoUploadedByName: null,
    customSubdomain: subdomain,
    portalUrl: `https://${subdomain}.myemployeesportal.com`,
    isPdfDownloadEnabled: true,
    isLocalizationEnabled: true,
    isFaqEnabled: true,
    isAvailableFormsEnabled: true,
    hasDraftChanges: false,
    lastPublishedBy: null,
    lastPublishedByName: null,
    lastPublishedDate: null,
  };
}

const MOCK_DELAY_MS = 300;

// In-memory store keyed by customerId so state persists across GET/PUT within a session.
const mockConfigStore = new Map<string, IPortalConfiguration>();

function getMockConfig(customerId: string): IPortalConfiguration {
  if (!mockConfigStore.has(customerId)) {
    mockConfigStore.set(customerId, buildPortalConfig(customerId));
  }
  return mockConfigStore.get(customerId)!;
}

function buildAvailableForms(customerId: string): IAvailableFormsResponse {
  return {
    customerId,
    isAvailableFormsEnabled: true,
    hasDraftChanges: false,
    forms: [
      { disclosureId: 'form-001', formName: 'Annual Ethics Declaration', isEnabled: true, isNewFromSource: false },
      { disclosureId: 'form-002', formName: 'Conflict of Interest Form', isEnabled: true, isNewFromSource: false },
      { disclosureId: 'form-003', formName: 'Gifts & Hospitality Register', isEnabled: false, isNewFromSource: true },
      { disclosureId: 'form-004', formName: 'Vendor Relationship Disclosure', isEnabled: false, isNewFromSource: false },
    ],
  };
}

function buildPublishResult(customerId: string): IPublishResult {
  const subdomain = `${customerId.toLowerCase()}-portal`;
  return {
    portalStatus: PortalStatus.Active,
    lastPublishedBy: 1,
    lastPublishedByName: 'Mock Admin',
    lastPublishedDate: new Date().toISOString(),
    portalUrl: `https://${subdomain}.myemployeesportal.com`,
  };
}

// ─── FAQ mock data ────────────────────────────────────────────────────────────

let mockFaqs: IFaqEntry[] = [
  {
    id: 'faq-001',
    sourceFaqId: null,
    question: 'What is the Employee Portal?',
    answer: '<p>The Employee Portal is a secure platform for submitting disclosures and managing compliance requirements.</p>',
    sortOrder: 1,
    isVisible: true,
    translations: [
      { id: 'tr-001', languageCode: 'es-es', languageName: 'Spanish', translatedQuestion: '¿Qué es el Portal de Empleados?', translatedAnswer: '<p>El Portal de Empleados es una plataforma segura para enviar divulgaciones.</p>' },
    ],
    modifiedBy: 1,
    modifiedAt: '2026-04-10T10:00:00Z',
  },
  {
    id: 'faq-002',
    sourceFaqId: null,
    question: 'How do I submit a disclosure?',
    answer: '<p>Navigate to the <b>Home</b> page and select the form you wish to complete. Fill in the required fields and click <b>Submit</b>.</p>',
    sortOrder: 2,
    isVisible: true,
    translations: [],
    modifiedBy: 1,
    modifiedAt: '2026-04-10T10:05:00Z',
  },
  {
    id: 'faq-003',
    sourceFaqId: null,
    question: 'Can I save my progress and return later?',
    answer: '<p>Yes. Click <b>Save Draft</b> at any time. Your progress will be saved and you can resume from where you left off.</p>',
    sortOrder: 3,
    isVisible: false,
    translations: [],
    modifiedBy: 1,
    modifiedAt: '2026-04-10T10:10:00Z',
  },
];

const MOCK_LANGUAGES: ILanguage[] = [
  { languageId: 1, languageCd: 'es-es', languageName: 'Spanish' },
  { languageId: 2, languageCd: 'fr-fr', languageName: 'French' },
  { languageId: 3, languageCd: 'de-de', languageName: 'German' },
  { languageId: 4, languageCd: 'ja-jp', languageName: 'Japanese' },
  { languageId: 5, languageCd: 'pt-pt', languageName: 'Portuguese' },
];

// ─── URL matchers ─────────────────────────────────────────────────────────────

function matchPortalConfig(url: string): boolean {
  // Matches: /api/v1/admin/customers/{id}/portal-config  (NOT /portal-config/*)
  return /\/api\/v1\/admin\/customers\/[^/]+\/portal-config$/.test(url);
}

function matchAvailableForms(url: string): boolean {
  return /\/api\/v1\/admin\/customers\/[^/]+\/portal-config\/available-forms$/.test(url);
}

function matchPublish(url: string): boolean {
  return /\/api\/v1\/admin\/customers\/[^/]+\/portal-config\/publish$/.test(url);
}

function matchDeactivate(url: string): boolean {
  return /\/api\/v1\/admin\/customers\/[^/]+\/portal-config\/deactivate$/.test(url);
}

function matchActivate(url: string): boolean {
  return /\/api\/v1\/admin\/customers\/[^/]+\/portal-config\/activate$/.test(url);
}

function matchPreview(url: string): boolean {
  return /\/api\/v1\/admin\/customers\/[^/]+\/portal-config\/preview$/.test(url);
}

function matchLogo(url: string): boolean {
  return /\/api\/v1\/admin\/customers\/[^/]+\/portal-config\/logo$/.test(url);
}

// FAQ matchers
function matchFaqsList(url: string): boolean {
  return /\/api\/v1\/admin\/customers\/[^/]+\/faqs$/.test(url);
}

function matchFaqsLanguages(url: string): boolean {
  return /\/api\/v1\/admin\/customers\/[^/]+\/faqs\/languages$/.test(url);
}

function matchFaqsOrder(url: string): boolean {
  return /\/api\/v1\/admin\/customers\/[^/]+\/faqs\/order$/.test(url);
}

function matchSingleFaq(url: string): RegExpMatchArray | null {
  return /\/api\/v1\/admin\/customers\/[^/]+\/faqs\/([^/]+)$/.exec(url);
}

function matchFaqVisibility(url: string): RegExpMatchArray | null {
  return /\/api\/v1\/admin\/customers\/[^/]+\/faqs\/([^/]+)\/visibility$/.exec(url);
}

function matchFaqTranslation(url: string): RegExpMatchArray | null {
  return /\/api\/v1\/admin\/customers\/[^/]+\/faqs\/([^/]+)\/translations\/([^/]+)$/.exec(url);
}

// ─── Interceptor ──────────────────────────────────────────────────────────────

export function mockApiInterceptor( // NOSONAR
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const env = inject(EnvironmentService);

  if (!env.useMockData) {
    return next(req);
  }

  const url = req.url;

  // Determine which base URL matches and extract the path portion.
  let path: string | null = null;
  if (env.employeePortalApiUrl && url.startsWith(env.employeePortalApiUrl)) {
    path = url.replace(env.employeePortalApiUrl, '');
  } else if (env.employeePortalApiUrl && url.startsWith(env.employeePortalApiUrl)) { // NOSONAR
    path = url.replace(env.employeePortalApiUrl, ''); // NOSONAR
  }

  if (path === null) {
    return next(req);
  }

  // GET portal-config
  if (req.method === 'GET' && matchPortalConfig(path)) {
    const customerId = extractCustomerId(path);
    const config = getMockConfig(customerId);
    debugLog('MockAPI', 'GET portal-config →', config);
    return of(new HttpResponse({ status: 200, body: config })).pipe(delay(MOCK_DELAY_MS));
  }

  // PUT portal-config (save draft)
  if (req.method === 'PUT' && matchPortalConfig(path)) {
    const customerId = extractCustomerId(path);
    const current = getMockConfig(customerId);
    const updated = { ...current, ...(req.body as object), hasDraftChanges: true };
    mockConfigStore.set(customerId, updated as IPortalConfiguration);
    debugLog('MockAPI', 'PUT portal-config →', updated);
    return of(new HttpResponse({ status: 200, body: updated })).pipe(delay(MOCK_DELAY_MS));
  }

  // GET available-forms
  if (req.method === 'GET' && matchAvailableForms(path)) {
    const customerId = extractCustomerId(path);
    const forms = buildAvailableForms(customerId);
    debugLog('MockAPI', 'GET available-forms →', forms);
    return of(new HttpResponse({ status: 200, body: forms })).pipe(delay(MOCK_DELAY_MS));
  }

  // PUT available-forms (save draft)
  if (req.method === 'PUT' && matchAvailableForms(path)) {
    const customerId = extractCustomerId(path);
    const body = req.body as any;
    const base = buildAvailableForms(customerId);
    const updated: IAvailableFormsResponse = {
      ...base,
      forms: body?.forms ?? base.forms,
      hasDraftChanges: true,
    };
    debugLog('MockAPI', 'PUT available-forms →', updated);
    return of(new HttpResponse({ status: 200, body: updated })).pipe(delay(MOCK_DELAY_MS));
  }

  // POST publish
  if (req.method === 'POST' && matchPublish(path)) {
    const customerId = extractCustomerId(path);
    const result = buildPublishResult(customerId);
    const current = getMockConfig(customerId);
    mockConfigStore.set(customerId, { ...current, portalStatus: PortalStatus.Active, hasDraftChanges: false, lastPublishedByName: result.lastPublishedByName, lastPublishedDate: result.lastPublishedDate });
    debugLog('MockAPI', 'POST publish →', result);
    return of(new HttpResponse({ status: 200, body: result })).pipe(delay(MOCK_DELAY_MS));
  }

  // POST deactivate
  if (req.method === 'POST' && matchDeactivate(path)) {
    const customerId = extractCustomerId(path);
    const current = getMockConfig(customerId);
    const deactivated = { ...current, portalStatus: PortalStatus.Deactivated };
    mockConfigStore.set(customerId, deactivated);
    debugLog('MockAPI', 'POST deactivate →', deactivated);
    return of(new HttpResponse({ status: 200, body: deactivated })).pipe(delay(MOCK_DELAY_MS));
  }

  // POST activate
  if (req.method === 'POST' && matchActivate(path)) {
    const customerId = extractCustomerId(path);
    const current = getMockConfig(customerId);
    const activated = { ...current, portalStatus: PortalStatus.Active };
    mockConfigStore.set(customerId, activated);
    debugLog('MockAPI', 'POST activate →', activated);
    return of(new HttpResponse({ status: 200, body: activated })).pipe(delay(MOCK_DELAY_MS));
  }

  // GET portal-config/preview
  if (req.method === 'GET' && matchPreview(path)) {
    const customerId = extractCustomerId(path);
    const cfg = getMockConfig(customerId);
    const forms = buildAvailableForms(customerId);
    const mockPreview: IPortalPreview = {
      previewUrl: `http://localhost:4900/${customerId}/portal-preview`,
      config: {
        customerId,
        portalStatus: cfg.portalStatus,
        customSubdomain: cfg.customSubdomain,
        portalUrl: cfg.portalUrl ?? '',
        isPdfDownloadEnabled: cfg.isPdfDownloadEnabled,
        isLocalizationEnabled: cfg.isLocalizationEnabled,
        isFaqEnabled: cfg.isFaqEnabled,
        isAvailableFormsEnabled: cfg.isAvailableFormsEnabled,
        availableLanguages: [
          { code: 'en-us', displayName: 'English' },
          { code: 'es-es', displayName: 'Spanish' },
        ],
      },
      faqs: mockFaqs,
      availableForms: forms.forms,
      logoUrl: cfg.logoUrl,
      logoFileSize: cfg.logoFileSize,
    };
    debugLog('MockAPI', 'GET portal-config/preview →', mockPreview);
    return of(new HttpResponse({ status: 200, body: mockPreview })).pipe(delay(MOCK_DELAY_MS));
  }

  // ─── FAQ endpoints ────────────────────────────────────────────────────────

  // GET /faqs/languages  (must come before the generic /faqs match)
  if (req.method === 'GET' && matchFaqsLanguages(path)) {
    debugLog('MockAPI', 'GET faqs/languages →', MOCK_LANGUAGES);
    return of(new HttpResponse({ status: 200, body: MOCK_LANGUAGES })).pipe(delay(MOCK_DELAY_MS));
  }

  // PUT /faqs/order
  if (req.method === 'PUT' && matchFaqsOrder(path)) {
    const { faqIds } = req.body as { faqIds: string[] };
    mockFaqs = faqIds.map((id, idx) => {
      const faq = mockFaqs.find(f => f.id === id)!;
      return { ...faq, sortOrder: idx + 1 };
    }).filter(Boolean);
    const result: IAdminFaqList = { faqs: mockFaqs, totalCount: mockFaqs.length };
    debugLog('MockAPI', 'PUT faqs/order →', result);
    return of(new HttpResponse({ status: 200, body: result })).pipe(delay(MOCK_DELAY_MS));
  }

  // PATCH /faqs/{id}/visibility
  const visMatch = matchFaqVisibility(path);
  if (req.method === 'PATCH' && visMatch) {
    const faqId = visMatch[1];
    const { isVisible } = req.body as { isVisible: boolean };
    const idx = mockFaqs.findIndex(f => f.id === faqId);
    if (idx >= 0) {
      mockFaqs[idx] = { ...mockFaqs[idx], isVisible };
      debugLog('MockAPI', `PATCH faqs/${faqId}/visibility →`, mockFaqs[idx]);
      return of(new HttpResponse({ status: 200, body: mockFaqs[idx] })).pipe(delay(MOCK_DELAY_MS));
    }
  }

  // PUT/DELETE /faqs/{id}/translations/{langCode}
  const trMatch = matchFaqTranslation(path);
  if (trMatch) {
    const faqId = trMatch[1];
    const langCode = trMatch[2];
    const faqIdx = mockFaqs.findIndex(f => f.id === faqId);
    if (faqIdx >= 0) {
      if (req.method === 'PUT') {
        const dto = req.body as { translatedQuestion: string; translatedAnswer: string };
        const existing = mockFaqs[faqIdx].translations.findIndex(t => t.languageCode === langCode);
        const langName = MOCK_LANGUAGES.find(l => l.languageCd === langCode)?.languageName ?? langCode;
        const tr = { id: `tr-${Date.now()}`, languageCode: langCode, languageName: langName, ...dto };
        if (existing >= 0) {
          mockFaqs[faqIdx].translations[existing] = tr;
        } else {
          mockFaqs[faqIdx].translations.push(tr);
        }
        debugLog('MockAPI', `PUT faqs/${faqId}/translations/${langCode} →`, tr);
        return of(new HttpResponse({ status: 200, body: tr })).pipe(delay(MOCK_DELAY_MS));
      }
      if (req.method === 'DELETE') {
        mockFaqs[faqIdx].translations = mockFaqs[faqIdx].translations.filter(t => t.languageCode !== langCode);
        debugLog('MockAPI', `DELETE faqs/${faqId}/translations/${langCode}`);
        return of(new HttpResponse({ status: 204, body: null })).pipe(delay(MOCK_DELAY_MS));
      }
    }
  }

  // GET/POST /faqs
  if (matchFaqsList(path)) {
    if (req.method === 'GET') {
      const result: IAdminFaqList = { faqs: mockFaqs, totalCount: mockFaqs.length };
      debugLog('MockAPI', 'GET faqs →', result);
      return of(new HttpResponse({ status: 200, body: result })).pipe(delay(MOCK_DELAY_MS));
    }
    if (req.method === 'POST') {
      const dto = req.body as { question: string; answer: string };
      const newFaq: IFaqEntry = {
        id: `faq-${Date.now()}`,
        sourceFaqId: null,
        question: dto.question,
        answer: dto.answer,
        sortOrder: mockFaqs.length + 1,
        isVisible: true,
        translations: [],
        modifiedBy: 1,
        modifiedAt: new Date().toISOString(),
      };
      mockFaqs.push(newFaq);
      debugLog('MockAPI', 'POST faqs →', newFaq);
      return of(new HttpResponse({ status: 201, body: newFaq })).pipe(delay(MOCK_DELAY_MS));
    }
  }

  // PUT/DELETE /faqs/{id}
  const faqMatch = matchSingleFaq(path);
  if (faqMatch) {
    const faqId = faqMatch[1];
    if (req.method === 'PUT') {
      const dto = req.body as { question: string; answer: string };
      const idx = mockFaqs.findIndex(f => f.id === faqId);
      if (idx >= 0) {
        mockFaqs[idx] = { ...mockFaqs[idx], ...dto, modifiedAt: new Date().toISOString() };
        debugLog('MockAPI', `PUT faqs/${faqId} →`, mockFaqs[idx]);
        return of(new HttpResponse({ status: 200, body: mockFaqs[idx] })).pipe(delay(MOCK_DELAY_MS));
      }
    }
    if (req.method === 'DELETE') {
      mockFaqs = mockFaqs.filter(f => f.id !== faqId);
      debugLog('MockAPI', `DELETE faqs/${faqId}`);
      return of(new HttpResponse({ status: 204, body: null })).pipe(delay(MOCK_DELAY_MS));
    }
  }

  // ─── Logo endpoints ─────────────────────────────────────────────────────────

  // GET /api/v1/forms/{disclosureId} — get available form detail
  if (req.method === 'GET' && /\/api\/v1\/forms\/[^/]+$/.test(path) && !path.includes('/draft')) {
    const discId = path.split('/').pop()!;
    const mockFormDetail = {
      disclosureId: discId,
      formName: 'Mock Available Form',
      formVersionId: `fv-${discId}`,
      schemaJson: '{"components":[]}',
      responseJson: null,
      isReadOnly: false,
      lastSavedAt: null,
      // Defaults: one-time, no pre-fill, no earlier submission.
      allowMultipleSubmissions: false,
      prefillEnabled: false,
      latestSubmission: null,
      prefill: null,
    };
    debugLog('MockAPI', 'GET forms detail → 200', mockFormDetail);
    return of(new HttpResponse({ status: 200, body: mockFormDetail })).pipe(delay(MOCK_DELAY_MS));
  }

  // PUT /api/v1/forms/{disclosureId}/draft — save available-form draft
  if (req.method === 'PUT' && /\/api\/v1\/forms\/[^/]+\/draft$/.test(path)) {
    const draftResult = { draftId: `draft-${Date.now()}`, updatedAt: new Date().toISOString() };
    debugLog('MockAPI', 'PUT forms draft → 200', draftResult);
    return of(new HttpResponse({ status: 200, body: draftResult })).pipe(delay(MOCK_DELAY_MS));
  }

  if (matchLogo(path)) {
    // POST /portal-config/logo — upload
    if (req.method === 'POST') {
      // Derive file name and a previewable object URL from the actual uploaded file so
      // the <img> tag renders the real image instead of a broken external placeholder.
      const formData = req.body as FormData;
      const uploadedFile = formData.get('file') as File | null;
      const fileName = uploadedFile?.name ?? 'mock-logo.png';
      const logoUrl = uploadedFile
        ? URL.createObjectURL(uploadedFile)
        : 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="60"><rect width="200" height="60" fill="%23eee"/><text x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14">COMPANY LOGO</text></svg>';
      const result: ILogoUploadResult = {
        fileName,
        logoUrl,
        uploadedAt: new Date().toISOString(),
        uploadedBy: 1,
        uploadedByName: 'Mock Admin',
        fileSize: uploadedFile?.size ?? 24576,
      };
      // Also update the in-memory portal config so subsequent GETs reflect the logo
      const logoCustomerId = extractCustomerId(path);
      const logoConfig = getMockConfig(logoCustomerId);
      mockConfigStore.set(logoCustomerId, {
        ...logoConfig,
        logoUrl: result.logoUrl,
        logoFileName: result.fileName,
        logoFileSize: result.fileSize,
        logoUploadedAt: result.uploadedAt,
        logoUploadedByName: result.uploadedByName ?? null,
      });
      debugLog('MockAPI', 'POST portal-config/logo →', result);
      return of(new HttpResponse({ status: 200, body: result })).pipe(delay(MOCK_DELAY_MS));
    }

    // DELETE /portal-config/logo — remove
    if (req.method === 'DELETE') {
      const logoCustomerId = extractCustomerId(path);
      const logoConfig = getMockConfig(logoCustomerId);
      mockConfigStore.set(logoCustomerId, {
        ...logoConfig,
        logoUrl: null,
        logoFileName: null,
        logoFileSize: null,
        logoUploadedAt: null,
        logoUploadedByName: null,
      });
      debugLog('MockAPI', 'DELETE portal-config/logo');
      return of(new HttpResponse({ status: 204, body: null })).pipe(delay(MOCK_DELAY_MS));
    }
  }

  // Fallthrough — pass unmatched requests through
  return next(req);
}
